import {useEffect, useRef, useState} from "react"
import {useApiService} from "./useApiService"
import {resolveEventTeam, searchTeamEvents} from "../services/eventSearch"
import {Event, EventTeam} from "../types/event"
import {TeamPreview} from "../types/team"

export type TeamEventsState = {
    /** Upcoming matches of the picked team, narrowed to the pair once a second one is picked */
    events: Event[]
    loading: boolean
    /** Names the picked teams the prediction database holds nothing for */
    unmatched: string[]
}

const EMPTY: TeamEventsState = {events: [], loading: false, unmatched: []}

/**
 * Upcoming matches of the teams picked on the analysis page.
 *
 * A pick loads its matches straight away, on whichever side of the fixture the team plays; picking a
 * second team appends its id to that search rather than starting a new one, leaving only the matches
 * the two play against each other.
 *
 * The prediction api has team ids of its own, so a picked team is first resolved by name against
 * /event-teams. Resolutions are kept for the lifetime of the page: re-picking a team already looked
 * up costs nothing.
 */
export const useTeamEvents = (home?: TeamPreview, away?: TeamPreview): TeamEventsState => {
    const apiService = useApiService()
    const [state, setState] = useState<TeamEventsState>(EMPTY)
    // Bumped per search so a slow answer cannot overwrite the results of a later one
    const runRef = useRef(0)
    const resolvedRef = useRef(new Map<string, EventTeam | undefined>())

    useEffect(() => {
        const picked = [home, away].filter(Boolean)

        if (!picked.length) {
            runRef.current += 1
            setState(EMPTY)
            return
        }

        const run = ++runRef.current
        const isStale = () => run !== runRef.current
        const newClient = () => apiService.client(true, false)

        const resolve = async (team: TeamPreview) => {
            const cache = resolvedRef.current

            if (!cache.has(team.name))
                cache.set(team.name, await resolveEventTeam(newClient, {name: team.name, teamId: team.id}))

            return cache.get(team.name)
        }

        setState(previous => ({...previous, loading: true}))

        ;(async () => {
            try {
                const resolved = await Promise.all(picked.map(resolve))
                if (isStale()) return

                const unmatched = picked.filter((_, index) => !resolved[index]).map(team => team.name)
                const [first, second] = resolved

                // A team with no counterpart cannot be searched for, and searching on the other one
                // alone would answer with matches that have nothing to do with the requested pair
                if (unmatched.length) {
                    setState({events: [], loading: false, unmatched})
                    return
                }

                const events = await searchTeamEvents(newClient, first.id, second?.id, "NOT_STARTED")
                if (isStale()) return

                setState({events, loading: false, unmatched: []})
            } catch (error) {
                if (!isStale()) setState(EMPTY)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [home?.id, home?.name, away?.id, away?.name])

    return state
}
